import React, { useState, useRef, useEffect, useContext } from "react";
import { connect } from "react-redux";
import MSpinner from "components/Shared/MSpinner.tsx";
import MSelect, { MSelectOption } from "components/Shared/MSelect.tsx";
import { Button, Checkbox, TextField } from "react-md";
import { StreetAddress, CustomerListResponse, CustomerInfo, SurveyTypeListDto, CopyCaseRequest } from "interfaces/Mueller.Business.Models";
import { userHasRight } from "selectors/rights";
import { CASE_DUPLICATE, CASE_DUPLICATE_WITH_ACCOUNT_SURVEY_TYPE, CASE_DUPLICATE_FOR_CUSTOMER, } from "typeConstants/rights";
import { getActiveSurveyTypesByCustomer } from "requests/customer";
import { copyCase, copyCaseAsCustomer } from "requests/homeowner/ho";
import responseHandler from "responseHandler";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider.tsx";

export interface CaseDuplicateProps {
  controlNumber: number;
  fetchingAccountList: boolean;
  accountList: CustomerListResponse[];
  portalTargetClass: string | undefined;  // Optional class name to use as a portal target if displaying in a dialog
  onFinished: () => void;   // Function to call after duplicating or cancelling (can be used to close dialog)
  // Redux
  currentInspectionType: SurveyTypeListDto;
  originalInspectionType: SurveyTypeListDto;
  currentAccount: CustomerInfo;
  canDuplicate: boolean;
  canDuplicateWithAccountSurveyType: boolean;
  canDuplicateAsCustomer: boolean;
  surveyAddress: StreetAddress;
  specialAttentionNotes: string;
  successToast: (text: string) => void;
  errorToast: (text: string) => void;
}

const SURVEY_TYPE_DROPDOWN_DIV_CLASSNAME = "case-duplicate-survey-type-selector";

const CaseDuplicate = (props: CaseDuplicateProps) => {
  const [copyCaseRequest, setCopyCaseRequest] = useState<CopyCaseRequest>();
  const [surveyTypeOptions, setSurveyTypeOptions] = useState<SurveyTypeListDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const dialog: DialogProviderValue = useContext(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {
    setCopyCaseRequest({ ...copyCaseRequest,
      controlIDNumber: props.controlNumber,
      webNotes: props.specialAttentionNotes,
      //@ts-ignore
      customerNumber: props.currentAccount.customerNumber,
      assignOriginalInspector: false,
      useOriginalDueDate: false,
      isRush: false
    });

    if (props.canDuplicateWithAccountSurveyType || props.canDuplicateAsCustomer) {
      getSurveyTypes(props.currentAccount.customerNumber);
    } else {
      // We don't need to wait on fetching survey types if the user can't select them anyway
      setIsLoading(false);
    }

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Set selection to current survey type if available in options
    if (surveyTypeOptions?.length) {
      let currentType = surveyTypeOptions.find(x => x.code === props.currentInspectionType.code || x.code === copyCaseRequest.surveyTypeCode);
      setCopyCaseRequest({ ...copyCaseRequest, surveyTypeCode: currentType ? currentType.code : null });
    }
  }, [surveyTypeOptions]);

  const isExternalUser = props.canDuplicateAsCustomer && !(props.canDuplicate || props.canDuplicateWithAccountSurveyType);

  const handleSave = () => {

    if (!copyCaseRequest?.customerNumber || !copyCaseRequest.surveyTypeCode) {
      props.errorToast("Please select a customer and survey type option");
      return;
    }

    const onSuccess = (responseControlIdNumber: number) => {
      dialog.showOk(
        `${isExternalUser ? "Case Re-Ordered" : "Case Copied"}`, 
        `Your order number is ${responseControlIdNumber}.`, 
        () => {
          props.onFinished();
        }
      );
    };
    const onError = () => {
      props.errorToast(`Error ${isExternalUser ? "Re-Ordering" : "Copying"} Case`);
      props.onFinished();
    };

    setIsLoading(true);

    if (isExternalUser) {
      copyCaseAsCustomer(copyCaseRequest, onSuccess, onError, mounted);
    } else {
      copyCase(copyCaseRequest, onSuccess, onError, mounted);
    }
  };

  const onCustomerChange = (newOption: MSelectOption) => {
    //@ts-ignore
    setCopyCaseRequest({ ...copyCaseRequest, customerNumber: newOption.value, surveyTypeCode: null});
    getSurveyTypes(newOption.value);

    //When selecting a new customer, make it clear that they have to select a new survey type. Not sure if there is a better way to do this
    const surveyTypeDropdownNode = document.querySelector(`.${SURVEY_TYPE_DROPDOWN_DIV_CLASSNAME} .react-select__single-value`);
    if (surveyTypeDropdownNode) {
      surveyTypeDropdownNode.innerHTML = "Select...";
    }
  };

  const getSurveyTypes = (customerNumber: number | string) => {
    const handleSurveyTypes = (response: SurveyTypeListDto[]) => {
      setSurveyTypeOptions(response);
      setIsLoading(false);
    };
    getActiveSurveyTypesByCustomer(customerNumber, handleSurveyTypes, mounted);
  };

  const onSurveyTypeChange = (newOption: MSelectOption) => {
    if (newOption) {
      //@ts-ignore
      setCopyCaseRequest({ ...copyCaseRequest, surveyTypeCode: newOption.value });
    }
    else {
      setCopyCaseRequest({ ...copyCaseRequest, surveyTypeCode: null});
    }
  };
  const onSpecialAttentionNotesChange = (value: string) =>
    setCopyCaseRequest({ ...copyCaseRequest, webNotes: value });
  const onDueDateChange = (value: boolean) =>
    setCopyCaseRequest({ ...copyCaseRequest, useOriginalDueDate: value });
  const onAssignFieldRepChange = (value: boolean) =>
    setCopyCaseRequest({ ...copyCaseRequest, assignOriginalInspector: value });
  const onRushChange = (value: boolean) => {
    setCopyCaseRequest({ ...copyCaseRequest, isRush: value });
  };

  const getAccountDefaultValue = () => {
    if (props.accountList.some(x => x.number === parseInt(props.currentAccount.customerNumber))) {
      const account = props.accountList.find(x => x.number === parseInt(props.currentAccount.customerNumber));
      return { label: account.displayLabel, value: account.number };
    }
    return null;
  };
  const accountDefaultValue = getAccountDefaultValue();

  const getSurveyTypeDefaultValue = () => {
    if (surveyTypeOptions.some(x => x.code === props.currentInspectionType.code)) {
      const surveyType = surveyTypeOptions.find(x => x.code === props.currentInspectionType.code);
      return { label: surveyType.name, value: surveyType.code };
    }
    return null;
  };
  const surveyTypeDefaultValue = getSurveyTypeDefaultValue();

  const portalTarget = props.portalTargetClass ? () => document.querySelector(`.${props.portalTargetClass}`) : null;

  if (isLoading || props.fetchingAccountList) return (<MSpinner />);

  const canEditSurveyTypeAndCustomer = props.canDuplicateWithAccountSurveyType || props.canDuplicateAsCustomer;

  return (
    <>
      <p>Control <b>{`${props.controlNumber}`}</b></p>
      <p>Survey Address <b>{`${props.surveyAddress.address1} ${props.surveyAddress.city}, ${props.surveyAddress.state} ${props.surveyAddress.zip}`}</b></p>
      {canEditSurveyTypeAndCustomer ?
        <>
          <p>Account</p>
          <MSelect
            options={props.accountList.map(x => ({ value: x.number, label: x.displayLabel }))}
            getMenuPortalTarget={portalTarget}
            onChange={onCustomerChange}
            defaultValue={accountDefaultValue}
            enableAllOption={false}
          />
          {copyCaseRequest?.customerNumber &&
            <div className={SURVEY_TYPE_DROPDOWN_DIV_CLASSNAME}>
              <p>Survey Type</p>
              <MSelect
                options={surveyTypeOptions.map(x => ({ value: x.code, label: x.name }))}
                getMenuPortalTarget={portalTarget}
                onChange={onSurveyTypeChange}
                defaultValue={surveyTypeDefaultValue}
                enableAllOption={false}
              />
            </div>
          }
        </>
        : <p>Survey Type <b>{`${props.originalInspectionType.name}`}</b></p>}
      <br />
      <TextField
        id="copy-dialog-special-attention-notes"
        maxLength={1000}
        rows={5}
        value={copyCaseRequest.webNotes || ""}
        onChange={onSpecialAttentionNotesChange}
        label="Edit Special Attention Notes"
      />
      {!isExternalUser &&
        <>
          <Checkbox
            id="copy-dialog-use-original-due-date"
            label="Use original case due date"
            onChange={onDueDateChange}
            checked={copyCaseRequest.useOriginalDueDate}
            name="Use original case due date"
          />
          <Checkbox
            id="copy-dialog-assign-original-field-rep"
            label="Assign to same field representative"
            onChange={onAssignFieldRepChange}
            checked={copyCaseRequest.assignOriginalInspector}
            name="Assign to same field representative"
          />
        </>
      }
      <Checkbox
        id="copy-dialog-rush"
        label="Rush"
        onChange={onRushChange}
        checked={copyCaseRequest.isRush}
        name="Rush"
      />
      <div className="copy-case-button-container">
        <Button onClick={props.onFinished}>
          Cancel
        </Button>
        <Button raised primary onClick={handleSave}>
          {isExternalUser ? "Re-Order" : "Duplicate"}
        </Button>
      </div>
    </>
  );

};

const mapStateToProps = (state) => {
  return {
    canDuplicateAsCustomer: userHasRight(CASE_DUPLICATE_FOR_CUSTOMER)(state),
    canDuplicateWithAccountSurveyType: userHasRight(CASE_DUPLICATE_WITH_ACCOUNT_SURVEY_TYPE)(state),
    canDuplicate: userHasRight(CASE_DUPLICATE)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  successToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
  errorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CaseDuplicate);
